import React, { useState, useEffect } from 'react';
import userService from '../services/userService';
import empresaService from '../services/empresaService';
import {
  accesoAlCambiarRol,
  rolRequiereEmpresaUnica,
  rolesParaCrearUsuario,
} from '../utils/rolUsuarioGestion';

const UserManagement = ({ user, onBack }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users');

  // Estados para modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Obtener usuario actual y permisos desde props
  const currentUser = user;
  const isSuperAdmin = user?.roles?.some(role => role.name === 'super_admin');
  const hasUsersRead = user?.permissions?.some(permission => permission.name === 'users.read');
  const hasUsersCreate = user?.permissions?.some(permission => permission.name === 'users.create');
  const hasUsersUpdate = user?.permissions?.some(permission => permission.name === 'users.update');
  const hasUsersDelete = user?.permissions?.some(permission => permission.name === 'users.delete');
  const [apiConnected, setApiConnected] = useState(null);

  console.log('👥 UserManagement - Usuario actual:', currentUser);
  console.log('🔑 UserManagement - Es Super Admin:', isSuperAdmin);
  console.log('📋 UserManagement - Permisos:', {
    read: hasUsersRead,
    create: hasUsersCreate,
    update: hasUsersUpdate,
    delete: hasUsersDelete
  });

  // Estados para formularios
  const [empresas, setEmpresas] = useState([]);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'admin',
    acceso: 'todas',
    empresa_id: ''
  });

  const [editUser, setEditUser] = useState({
    name: '',
    email: '',
    role: '',
    acceso: 'todas',
    empresa_id: ''
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Cargar usuarios desde la API
  useEffect(() => {
    if (hasUsersRead || isSuperAdmin) {
    loadUsers();
      loadRoles();
      loadPermissions();
      loadEmpresas();
    }
    checkApiStatus();
  }, [hasUsersRead, isSuperAdmin]);

  const loadEmpresas = async () => {
    try {
      const response = await empresaService.listarEmpresas();
      setEmpresas(response.data || []);
    } catch (error) {
      setEmpresas([]);
    }
  };

  const checkApiStatus = async () => {
    try {
      const status = await userService.checkApiConnection();
      setApiConnected(status.connected);
    } catch (error) {
      setApiConnected(false);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await userService.getAllUsers();
      const usersData = response.data || response || [];
      setUsers(usersData);
      console.log('✅ Usuarios cargados desde API:', usersData.length);
    } catch (error) {
      console.error('❌ Error cargando usuarios:', error.message);
      
      // Sin fallback - mostrar solo error
      setUsers([]);
      
      if (error.message.includes('permisos')) {
        alert(`❌ ${error.message}`);
      } else {
        alert('❌ Error al conectar con la API. No se pudieron cargar los usuarios.');
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await userService.getAllRoles();
      const rolesData = response.data || response || [];
      setRoles(rolesData);
      console.log('✅ Roles cargados desde API:', rolesData.length);
    } catch (error) {
      console.error('❌ Error cargando roles:', error.message);
      // Sin fallback - array vacío
      setRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const loadPermissions = async () => {
    setLoadingPermissions(true);
    try {
      const response = await userService.getAllPermissions();
      const permissionsData = response.data || response || [];
      setPermissions(permissionsData);
      console.log('✅ Permisos cargados desde API:', permissionsData.length);
    } catch (error) {
      console.error('❌ Error cargando permisos:', error.message);
      // Sin fallback - array vacío
      setPermissions([]);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const getRoleName = (roleName) => {
    return roles.find(r => r.name === roleName)?.display_name || roleName;
  };

  const getRoleColor = (roleName) => {
    // Colores por defecto basados en el nombre del rol
    const colorMap = {
      'super_admin': 'bg-red-100 text-red-800',
      'admin': 'bg-orange-100 text-orange-800',
      'hr_manager': 'bg-blue-100 text-blue-800',
      'accountant': 'bg-green-100 text-green-800',
      'supervisor': 'bg-purple-100 text-purple-800',
      'ssoma': 'bg-teal-100 text-teal-800',
      'user': 'bg-gray-100 text-gray-800'
    };
    return colorMap[roleName] || 'bg-gray-100 text-gray-800';
  };

  // Función para obtener el primer rol de un usuario (para mostrar)
  const getUserMainRole = (user) => {
    return user.roles && user.roles.length > 0 ? user.roles[0] : null;
  };

  // Función para obtener todos los nombres de roles de un usuario
  const getUserRoleNames = (user) => {
    return user.roles?.map(role => role.display_name).join(', ') || 'Sin rol';
  };

  // Funciones para manejar formularios
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Verificar permisos
    if (!hasUsersCreate && !isSuperAdmin) {
      alert('❌ No tienes permisos para crear usuarios');
      return;
    }

    if (newUser.password !== newUser.confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }

    if (newUser.password.length < 8) {
      alert('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    if ((newUser.acceso === 'una' || rolRequiereEmpresaUnica(newUser.role)) && !newUser.empresa_id) {
      alert('Selecciona la empresa a la que pertenecerá este usuario');
      return;
    }

    setLoading(true);
    
    try {
      const response = await userService.createUser({
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        empresa_id: (newUser.acceso === 'una' || rolRequiereEmpresaUnica(newUser.role)) ? newUser.empresa_id : null
      });
      
      console.log('✅ Usuario creado:', response);
      
      // Recargar la lista de usuarios
      await loadUsers();
      setNewUser({ name: '', email: '', password: '', confirmPassword: '', role: 'admin', acceso: 'todas', empresa_id: '' });
      setShowCreateModal(false);
      alert('✅ Usuario creado exitosamente');
    } catch (error) {
      console.error('❌ Error creando usuario:', error);
      alert(`❌ ${error.message || 'Error al crear usuario'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await userService.updateUser(selectedUser.id, {
        name: editUser.name,
        email: editUser.email,
        role: editUser.role,
        empresa_id: (editUser.acceso === 'una' || rolRequiereEmpresaUnica(editUser.role)) ? editUser.empresa_id : null
      });
      
      // Recargar la lista de usuarios
      await loadUsers();
      setShowEditModal(false);
      alert('✅ Usuario actualizado exitosamente');
    } catch (error) {
      console.error('❌ Error actualizando usuario:', error);
      alert(`❌ ${error.message || 'Error al actualizar usuario'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      alert('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);
    
    try {
      await userService.changePassword(selectedUser.id, {
        password: passwordData.newPassword
      });
      
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setShowPasswordModal(false);
      alert(`✅ Contraseña cambiada exitosamente para ${selectedUser.name}`);
    } catch (error) {
      console.error('❌ Error cambiando contraseña:', error);
      alert(`❌ ${error.message || 'Error al cambiar contraseña'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    const hasSuperAdminRole = user.roles?.some(role => role.name === 'super_admin');
    if (hasSuperAdminRole) {
      alert('No se puede eliminar al Super Administrador');
      return;
    }
    
    if (!window.confirm(`¿Estás seguro de eliminar al usuario ${user.name}?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    setLoading(true);
    
    try {
      await userService.deleteUser(user.id);
      
      // Recargar la lista de usuarios
      await loadUsers();
      alert('✅ Usuario eliminado exitosamente');
    } catch (error) {
      console.error('❌ Error eliminando usuario:', error);
      alert(`❌ ${error.message || 'Error al eliminar usuario'}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (user) => {
    const hasSuperAdminRole = user.roles?.some(role => role.name === 'super_admin');
    if (hasSuperAdminRole) {
      alert('No se puede cambiar el estado del Super Administrador');
      return;
    }

    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activar' : 'desactivar';
    
    if (!window.confirm(`¿Estás seguro de ${action} al usuario ${user.name}?`)) {
      return;
    }

    try {
      await userService.updateUserStatus(user.id, newStatus);
      
      // Recargar la lista de usuarios
      await loadUsers();
      alert(`✅ Usuario ${action} exitosamente`);
    } catch (error) {
      console.error('❌ Error cambiando estado del usuario:', error);
      alert(`❌ ${error.message || 'Error al cambiar estado del usuario'}`);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    // Obtener el primer rol del usuario (o rol por defecto)
    const userRole = user.roles && user.roles.length > 0 ? user.roles[0].name : 'user';
    setEditUser({
      name: user.name,
      email: user.email,
      role: userRole,
      acceso: user.empresa_id ? 'una' : 'todas',
      empresa_id: user.empresa_id || ''
    });
    setShowEditModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setPasswordData({ newPassword: '', confirmPassword: '' });
    setShowPasswordModal(true);
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowPasswordModal(false);
    setShowPermissionModal(false);
    setSelectedUser(null);
    setSelectedRole(null);
    setNewUser({ name: '', email: '', password: '', confirmPassword: '', role: 'admin', acceso: 'todas', empresa_id: '' });
    setEditUser({ name: '', email: '', role: '', acceso: 'todas', empresa_id: '' });
    setPasswordData({ newPassword: '', confirmPassword: '' });
  };

  // Funciones de gestión de permisos
  const openPermissionModal = async (role) => {
    setSelectedRole(role);
    setShowPermissionModal(true);
    
    // Cargar todos los permisos disponibles para asegurar información actualizada
    await loadPermissions();
  };

  const handleAssignPermission = async (permissionId) => {
    if (!selectedRole) return;

    setLoading(true);
    try {
      await userService.assignPermissionToRole(selectedRole.id, permissionId);
      // Recargar roles y permisos para reflejar los cambios
      await Promise.all([loadRoles(), loadPermissions()]);
      
      // Actualizar el rol seleccionado con los nuevos datos
      const updatedRoles = await userService.getAllRoles();
      const updatedRole = (updatedRoles.data || updatedRoles).find(r => r.id === selectedRole.id);
      if (updatedRole) {
        setSelectedRole(updatedRole);
      }
      
      alert('✅ Permiso asignado exitosamente');
    } catch (error) {
      console.error('❌ Error asignando permiso:', error);
      alert(`❌ ${error.message || 'Error al asignar permiso'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePermission = async (permissionId) => {
    if (!selectedRole) return;

    if (!window.confirm('¿Estás seguro de quitar este permiso del rol?')) {
      return;
    }

    setLoading(true);
    try {
      await userService.removePermissionFromRole(selectedRole.id, permissionId);
      // Recargar roles y permisos para reflejar los cambios
      await Promise.all([loadRoles(), loadPermissions()]);
      
      // Actualizar el rol seleccionado con los nuevos datos
      const updatedRoles = await userService.getAllRoles();
      const updatedRole = (updatedRoles.data || updatedRoles).find(r => r.id === selectedRole.id);
      if (updatedRole) {
        setSelectedRole(updatedRole);
      }
      
      alert('✅ Permiso removido exitosamente');
    } catch (error) {
      console.error('❌ Error removiendo permiso:', error);
      alert(`❌ ${error.message || 'Error al remover permiso'}`);
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar si un rol tiene un permiso específico
  const roleHasPermission = (role, permissionId) => {
    return role.permissions?.some(p => p.id === permissionId);
  };



  // Si no es super administrador, mostrar mensaje de acceso denegado
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 p-4 lg:p-8 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
          <p className="text-gray-600 mb-6">No tienes permisos para acceder a la gestión de usuarios. Solo el Super Administrador puede ver esta sección.</p>
          <button
            onClick={onBack}
            className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Volver</span>
              </button>
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-3xl font-bold text-gray-900">Gestión de Usuarios</h1>
                  {apiConnected !== null && (
                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                      apiConnected 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${
                        apiConnected ? 'bg-green-500' : 'bg-yellow-500'
                      }`}></div>
                      <span>{apiConnected ? 'API Conectada' : 'Modo Offline'}</span>
                    </div>
                  )}
                </div>
                <p className="text-gray-600 mt-1">
                  Administra usuarios, roles y permisos del sistema
                  {!apiConnected && apiConnected !== null && ' (Datos de ejemplo)'}
                </p>
              </div>
            </div>
            {isSuperAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 py-2 rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Crear Usuario</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Usuarios</p>
                {loadingUsers ? (
                  <div className="h-8 bg-gray-300 rounded w-16 animate-pulse"></div>
                ) : (
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                )}
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Usuarios de empresa</p>
                {loadingUsers ? (
                  <div className="h-8 bg-gray-300 rounded w-16 animate-pulse"></div>
                ) : (
                <p className="text-2xl font-bold text-gray-900">{users.filter((u) => u.empresa_id).length}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Solo ven una empresa</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
          </div>
              </div>

        {/* Pestañas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('users')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'users'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                  <span>Usuarios ({loadingUsers ? '...' : users.length})</span>
              </div>
              </button>
              <button
                onClick={() => setActiveTab('empresa-users')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'empresa-users'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>Usuarios de empresa ({loadingUsers ? '...' : users.filter((u) => u.empresa_id).length})</span>
              </div>
              </button>
              <button
                onClick={() => setActiveTab('roles')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'roles'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                  <span>Roles y Permisos ({loadingRoles ? '...' : roles.length})</span>
              </div>
              </button>
            </nav>
            </div>
          </div>

        {/* Contenido de las pestañas */}
        {(activeTab === 'users' || activeTab === 'empresa-users') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {activeTab === 'empresa-users' ? 'Usuarios de empresa' : 'Lista de Usuarios'}
              </h2>
              {activeTab === 'empresa-users' && (
                <p className="text-sm text-gray-500 mt-1">Estos usuarios entran directo a su empresa y no pueden cambiar a otra.</p>
              )}
            </div>
            {activeTab === 'empresa-users' && isSuperAdmin && (
              <button
                onClick={() => {
                  setNewUser({ name: '', email: '', password: '', confirmPassword: '', role: 'admin', acceso: 'una', empresa_id: '' });
                  setShowCreateModal(true);
                }}
                className="text-sm bg-teal-600 text-white px-3 py-2 rounded-lg hover:bg-teal-700"
              >
                Crear usuario de empresa
              </button>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingUsers ? (
                  // Skeleton loading para usuarios
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`skeleton-${index}`} className="animate-pulse">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                          <div className="ml-4 space-y-2">
                            <div className="h-4 bg-gray-300 rounded w-32"></div>
                            <div className="h-3 bg-gray-200 rounded w-48"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-1">
                          <div className="h-6 bg-gray-300 rounded-full w-20"></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-6 bg-gray-300 rounded w-28"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-gray-300 rounded"></div>
                          <div className="w-6 h-6 bg-gray-300 rounded"></div>
                          <div className="w-6 h-6 bg-gray-300 rounded"></div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (activeTab === 'empresa-users' ? users.filter((u) => u.empresa_id) : users).length > 0 ? (
                  (activeTab === 'empresa-users' ? users.filter((u) => u.empresa_id) : users).map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {getUserRoleNames(user) !== 'Sin rol' ? (
                            user.roles?.map((role) => (
                              <span key={role.id} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(role.name)}`}>
                                {role.display_name}
                      </span>
                            ))
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Sin rol
                      </span>
                          )}
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.empresa_id ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.empresa?.name || `Empresa #${user.empresa_id}`}</div>
                          {user.empresa?.ruc && <div className="text-xs text-gray-500">RUC: {user.empresa.ruc}</div>}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          Todas las empresas
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {/* Editar: Solo Super Admin */}
                        {isSuperAdmin && (
                          <button
                            onClick={() => openEditModal(user)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="Editar usuario"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        
                        {/* Cambiar contraseña: Super Admin o propio usuario */}
                        {(isSuperAdmin || currentUser.id === user.id) && (
                          <button
                            onClick={() => openPasswordModal(user)}
                            className="text-yellow-600 hover:text-yellow-900 p-1 rounded"
                            title="Cambiar contraseña"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1721 9z" />
                            </svg>
                          </button>
                        )}
                        
                          {/* Eliminar: Solo Super Admin y no Super Admin */}
                          {isSuperAdmin && !user.roles?.some(role => role.name === 'super_admin') && (
                          <button
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600 hover:text-red-900 p-1 rounded"
                              title="Eliminar usuario"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                              </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {activeTab === 'empresa-users' ? 'No hay usuarios de empresa' : 'No hay usuarios'}
                        </h3>
                        <p className="text-gray-500">
                          {activeTab === 'empresa-users'
                            ? 'Crea un usuario y asígnalo a una empresa para que solo vea esa empresa.'
                            : 'No se encontraron usuarios en el sistema.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Pestaña de Roles y Permisos */}
        {activeTab === 'roles' && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Header de la tabla */}
            <div className="bg-gradient-to-r from-teal-500 to-cyan-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white">Gestión de Roles y Permisos</h3>
              <p className="text-teal-50 text-sm mt-1">Configura los permisos para cada rol del sistema</p>
            </div>

            {/* Tabla de roles y permisos */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 w-1/3">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>Rol</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span>Permisos Asignados</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 w-32">
                      <span>Acciones</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loadingRoles ? (
                    // Skeleton loading para roles
                    Array.from({ length: 3 }).map((_, index) => (
                      <tr key={`role-skeleton-${index}`} className="animate-pulse">
                        <td className="px-6 py-6">
                          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-gray-300">
                            <div className="flex items-center space-x-4">
                              <div className="w-6 h-6 bg-gray-300 rounded"></div>
                              <div className="flex-1 space-y-2">
                                <div className="h-5 bg-gray-300 rounded w-32"></div>
                                <div className="h-4 bg-gray-200 rounded w-48"></div>
                                <div className="flex items-center space-x-2 mt-2">
                                  <div className="h-5 bg-gray-300 rounded w-16"></div>
                                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-6">
                          <div className="flex flex-wrap gap-2">
                            <div className="h-6 bg-gray-300 rounded-full w-20"></div>
                            <div className="h-6 bg-gray-300 rounded-full w-24"></div>
                            <div className="h-6 bg-gray-300 rounded-full w-16"></div>
                          </div>
                        </td>
                        <td className="px-6 py-6 text-center">
                          <div className="w-12 h-12 bg-gray-300 rounded-xl mx-auto"></div>
                        </td>
                      </tr>
                    ))
                  ) : roles.length > 0 ? (
                    roles.map((role, index) => {
                    const roleColors = {
                      'super_admin': 'bg-red-50 border-l-red-500 text-red-700',
                      'admin': 'bg-orange-50 border-l-orange-500 text-orange-700',
                      'hr_manager': 'bg-blue-50 border-l-blue-500 text-blue-700',
                      'accountant': 'bg-emerald-50 border-l-emerald-500 text-emerald-700',
                      'supervisor': 'bg-purple-50 border-l-purple-500 text-purple-700',
                      'ssoma': 'bg-teal-50 border-l-teal-500 text-teal-700',
                      'user': 'bg-gray-50 border-l-gray-500 text-gray-700'
                    };

                    const roleIcons = {
                      'super_admin': (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      ),
                      'admin': (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      ),
                      'hr_manager': (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                      ),
                      'accountant': (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      ),
                      'supervisor': (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ),
                      'ssoma': (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      ),
                      'user': (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      )
                    };

                    return (
                      <tr key={role.id} className="hover:bg-gray-50 transition-colors duration-200">
                        {/* Columna del Rol */}
                        <td className="px-6 py-6">
                          <div className={`${roleColors[role.name] || roleColors['user']} rounded-xl p-4 border-l-4`}>
                            <div className="flex items-center space-x-4">
                              <div className="flex-shrink-0">
                                {roleIcons[role.name] || roleIcons['user']}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-lg font-bold truncate">
                                  {role.display_name}
                                </h4>
                                <p className="text-sm opacity-80 mt-1 leading-relaxed">
                                  {role.description}
                                </p>
                                <div className="mt-2 flex items-center space-x-2">
                                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-white/60">
                                    {role.name}
                                  </span>
                                  <span className="text-xs opacity-75">
                                    {role.permissions?.length || 0} permisos
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Columna de Permisos */}
                        <td className="px-6 py-6">
                          {role.permissions && role.permissions.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {role.permissions.slice(0, 5).map((permission) => (
                                <span
                                  key={permission.id}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 border border-teal-200"
                                  title={permission.description}
                                >
                                  {permission.display_name}
                                </span>
                              ))}
                              {role.permissions.length > 5 && (
                                <button
                                  onClick={() => openPermissionModal(role)}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 transition-colors duration-200"
                                  title="Ver todos los permisos"
                                >
                                  +{role.permissions.length - 5} más
                          </button>
                        )}
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                              </div>
                              <p className="text-gray-500 text-xs font-medium">Sin permisos</p>
                              <p className="text-gray-400 text-xs">Haz clic en gestionar</p>
                            </div>
                          )}
                        </td>

                        {/* Columna de Acciones */}
                        <td className="px-6 py-6 text-center">
                          <button
                            onClick={() => openPermissionModal(role)}
                            className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl"
                            title="Gestionar permisos"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay roles</h3>
                        <p className="text-gray-500">No se encontraron roles en el sistema.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

            {/* Footer con estadísticas */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {loadingRoles ? (
                    <div className="h-4 bg-gray-300 rounded w-32 animate-pulse"></div>
                  ) : (
                    <span><span className="font-medium">{roles.length} roles</span> configurados en el sistema</span>
                  )}
        </div>
                <div className="text-sm text-gray-500">
                  {loadingPermissions ? (
                    <div className="h-4 bg-gray-300 rounded w-40 animate-pulse"></div>
                  ) : (
                    <span>Total de permisos disponibles: <span className="font-medium text-gray-700">{permissions.length}</span></span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Crear Usuario */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Crear Nuevo Usuario</h3>
              <button
                onClick={closeAllModals}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                <input
                  type="text"
                  required
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                {loadingRoles ? (
                  <div className="w-full h-10 bg-gray-300 rounded-md animate-pulse"></div>
                ) : (
                <select
                  value={newUser.role}
                  onChange={(e) => {
                    const role = e.target.value;
                    setNewUser({
                      ...newUser,
                      role,
                      acceso: accesoAlCambiarRol(role, newUser.acceso),
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                    {rolesParaCrearUsuario(roles).map(role => (
                      <option key={role.id} value={role.name}>{role.display_name}</option>
                  ))}
                </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Acceso a empresas</label>
                <select
                  value={rolRequiereEmpresaUnica(newUser.role) ? 'una' : newUser.acceso}
                  onChange={(e) => setNewUser({...newUser, acceso: e.target.value, empresa_id: e.target.value === 'todas' ? '' : newUser.empresa_id})}
                  disabled={rolRequiereEmpresaUnica(newUser.role)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                >
                  {!rolRequiereEmpresaUnica(newUser.role) && (
                    <option value="todas">Todas las empresas</option>
                  )}
                  <option value="una">Solo una empresa</option>
                </select>
                {rolRequiereEmpresaUnica(newUser.role) && (
                  <p className="text-xs text-gray-500 mt-1">El usuario SSOMA entra solo al módulo SSOMA de una empresa.</p>
                )}
              </div>
              {(newUser.acceso === 'una' || rolRequiereEmpresaUnica(newUser.role)) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                  <select
                    required
                    value={newUser.empresa_id}
                    onChange={(e) => setNewUser({...newUser, empresa_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Seleccionar empresa</option>
                    {empresas.map((empresa) => (
                      <option key={empresa.id} value={empresa.id}>{empresa.name} — {empresa.ruc}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Este usuario solo verá esa empresa y no podrá cambiar a otra.</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newUser.confirmPassword}
                  onChange={(e) => setNewUser({...newUser, confirmPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
                >
                  {loading ? 'Creando...' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Usuario */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Editar Usuario</h3>
              <button
                onClick={closeAllModals}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleEditUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                <input
                  type="text"
                  required
                  value={editUser.name}
                  onChange={(e) => setEditUser({...editUser, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={editUser.email}
                  onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                {loadingRoles ? (
                  <div className="w-full h-10 bg-gray-300 rounded-md animate-pulse"></div>
                ) : (
                <select
                  value={editUser.role}
                  onChange={(e) => {
                    const role = e.target.value;
                    setEditUser({
                      ...editUser,
                      role,
                      acceso: accesoAlCambiarRol(role, editUser.acceso),
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={selectedUser.role === 'super_admin'}
                >
                    {roles.filter(role => role.name !== 'super_admin' || selectedUser.role === 'super_admin').map(role => (
                      <option key={role.id} value={role.name}>{role.display_name}</option>
                  ))}
                </select>
                )}
              </div>
              {selectedUser.role !== 'super_admin' && !selectedUser.roles?.some((role) => role.name === 'super_admin') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Acceso a empresas</label>
                    <select
                      value={rolRequiereEmpresaUnica(editUser.role) ? 'una' : editUser.acceso}
                      onChange={(e) => setEditUser({...editUser, acceso: e.target.value, empresa_id: e.target.value === 'todas' ? '' : editUser.empresa_id})}
                      disabled={rolRequiereEmpresaUnica(editUser.role)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                    >
                      {!rolRequiereEmpresaUnica(editUser.role) && (
                        <option value="todas">Todas las empresas</option>
                      )}
                      <option value="una">Solo una empresa</option>
                    </select>
                    {rolRequiereEmpresaUnica(editUser.role) && (
                      <p className="text-xs text-gray-500 mt-1">El usuario SSOMA entra solo al módulo SSOMA de una empresa.</p>
                    )}
                  </div>
                  {(editUser.acceso === 'una' || rolRequiereEmpresaUnica(editUser.role)) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                      <select
                        required
                        value={editUser.empresa_id}
                        onChange={(e) => setEditUser({...editUser, empresa_id: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Seleccionar empresa</option>
                        {empresas.map((empresa) => (
                          <option key={empresa.id} value={empresa.id}>{empresa.name} — {empresa.ruc}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedUser.role === 'super_admin'}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
                >
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Cambiar Contraseña */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Cambiar Contraseña - {selectedUser.name}
              </h3>
              <button
                onClick={closeAllModals}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar nueva contraseña</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
                >
                  {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Gestionar Permisos */}
      {showPermissionModal && selectedRole && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            {/* Header con gradiente */}
            <div className="bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 p-8 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold">Gestionar Permisos</h3>
                      <p className="text-white/90 mt-1 text-lg">
                        Configurando permisos para: <span className="font-semibold">{selectedRole.display_name}</span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeAllModals}
                    className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Elementos decorativos */}
              <div className="absolute top-4 right-20 w-24 h-24 bg-white/5 rounded-full"></div>
              <div className="absolute bottom-4 left-20 w-16 h-16 bg-white/5 rounded-full"></div>
            </div>

            {/* Contenido scrolleable */}
            <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-8">
              {/* Sección de permisos actuales del rol */}
              {selectedRole.permissions && selectedRole.permissions.length > 0 ? (
                <div className="mb-8 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-emerald-800">Permisos Actuales del Rol</h3>
                        <p className="text-emerald-600 text-sm">
                          {selectedRole.permissions.length} permisos asignados actualmente
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-emerald-700">
                        {selectedRole.permissions.length}
                      </div>
                      <div className="text-xs text-emerald-600">permisos</div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {selectedRole.permissions.map((permission) => (
                      <button
                        key={permission.id}
                        onClick={() => handleRemovePermission(permission.id)}
                        disabled={loading}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-red-100 hover:text-red-800 hover:border-red-300 transition-all duration-200 disabled:opacity-50 group"
                        title={`Clic para remover: ${permission.description}`}
                      >
                        <svg className="w-3 h-3 mr-1 group-hover:hidden" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <svg className="w-3 h-3 mr-1 hidden group-hover:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                        {permission.display_name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mb-8 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border-2 border-amber-200">
                  <div className="flex items-center justify-center space-x-4">
                    <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <h3 className="text-lg font-bold text-amber-800">Sin Permisos Asignados</h3>
                      <p className="text-amber-600 text-sm">
                        Este rol no tiene permisos asignados. Usa la sección de abajo para asignar permisos.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sección de permisos no asignados */}
              {(() => {
                const unassignedPermissions = permissions.filter(permission => 
                  !roleHasPermission(selectedRole, permission.id)
                );
                
                return unassignedPermissions.length > 0 ? (
                  <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-blue-800">Permisos Disponibles para Asignar</h3>
                          <p className="text-blue-600 text-sm">
                            {unassignedPermissions.length} permisos disponibles para asignar a este rol
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-700">
                          {unassignedPermissions.length}
                        </div>
                        <div className="text-xs text-blue-600">disponibles</div>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {unassignedPermissions.map((permission) => (
                        <button
                          key={permission.id}
                          onClick={() => handleAssignPermission(permission.id)}
                          disabled={loading}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-all duration-200 disabled:opacity-50"
                          title={`Clic para asignar: ${permission.description}`}
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          {permission.display_name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200">
                    <div className="flex items-center justify-center space-x-4">
                      <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="text-center">
                        <h3 className="text-lg font-bold text-green-800">¡Todos los Permisos Asignados!</h3>
                        <p className="text-green-600 text-sm">
                          Este rol tiene todos los permisos disponibles en el sistema.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}


            </div>


          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 